"use client"
import React, { useState } from "react"
import { useTheme, alpha } from "@mui/material"

const Table = ({ columns, data, pagination = true, itemsPerPage = 5 }) => {
  const [currentPage, setCurrentPage] = useState(1)
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "ascending" })
  const theme = useTheme()

  // Sorting logic
  const sortedData = React.useMemo(() => {
    const sortableData = [...data]
    if (sortConfig.key) {
      sortableData.sort((a, b) => {
        if (a[sortConfig.key] < b[sortConfig.key]) {
          return sortConfig.direction === "ascending" ? -1 : 1
        }
        if (a[sortConfig.key] > b[sortConfig.key]) {
          return sortConfig.direction === "ascending" ? 1 : -1
        }
        return 0
      })
    }
    return sortableData
  }, [data, sortConfig])

  // Pagination logic
  const totalPages = Math.ceil(sortedData.length / itemsPerPage)
  const indexOfLastItem = currentPage * itemsPerPage
  const indexOfFirstItem = indexOfLastItem - itemsPerPage
  const currentItems = sortedData.slice(indexOfFirstItem, indexOfLastItem)

  const requestSort = (key) => {
    let direction = "ascending"
    if (sortConfig.key === key && sortConfig.direction === "ascending") {
      direction = "descending"
    }
    setSortConfig({ key, direction })
  }

  return (
    <div>
      <div style={{ overflowX: "auto" }}>
        <table
          style={{
            width: "100%",
            borderCollapse: "collapse",
            fontSize: "14px",
            color: theme.palette.text.primary,
          }}
        >
          <thead>
            <tr style={{ background: theme.palette.mode === 'dark' ? '#2A2A2A' : '#F5F5F5' }}>
              {columns.map((column) => (
                <th
                  key={column.key}
                  onClick={() => column.sortable && requestSort(column.key)}
                  style={{
                    padding: "16px 15px",
                    textAlign: "left",
                    color: theme.palette.primary.main,
                    fontWeight: 800,
                    borderBottom: `2px solid ${theme.palette.divider}`,
                    cursor: column.sortable ? "pointer" : "default",
                    position: "relative",
                  }}
                >
                  {column.label}
                  {sortConfig.key === column.key && (
                    <span style={{ marginLeft: "5px" }}>{sortConfig.direction === "ascending" ? "↑" : "↓"}</span>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {currentItems.length > 0 ? (
              currentItems.map((row, index) => (
                <tr
                  key={index}
                  style={{
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    background: index % 2 === 0 ? "transparent" : alpha(theme.palette.primary.main, 0.02),
                    transition: "all 0.2s ease",
                  }}
                >
                  {columns.map((column) => (
                    <td
                      key={column.key}
                      style={{
                        padding: "14px 15px",
                        textAlign: column.align || "left",
                        color: theme.palette.text.primary,
                        fontWeight: 500,
                      }}
                    >
                      {column.render ? column.render(row, indexOfFirstItem + index) : row[column.key]}
                    </td>
                  ))}
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={columns.length} style={{ padding: "40px", textAlign: "center", color: theme.palette.text.secondary }}>
                  No records found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {pagination && totalPages > 1 && (
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginTop: "24px",
            padding: "0 10px",
          }}
        >
          <div style={{ color: theme.palette.text.secondary, fontSize: "0.9rem", fontWeight: 600 }}>
            Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, data.length)} of {data.length} entries
          </div>
          <div style={{ display: "flex", gap: "8px" }}>
            <button
              onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              style={{
                padding: "8px 16px",
                backgroundColor: theme.palette.background.paper,
                color: currentPage === 1 ? theme.palette.text.disabled : theme.palette.primary.main,
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: "8px",
                cursor: currentPage === 1 ? "not-allowed" : "pointer",
                fontWeight: 700,
                transition: "all 0.2s",
              }}
            >
              Previous
            </button>
            <div style={{ display: "flex", gap: "5px" }}>
              {(() => {
                const pages = [];
                const maxVisible = 5;
                
                if (totalPages <= maxVisible + 2) {
                  for (let i = 1; i <= totalPages; i++) pages.push(i);
                } else {
                  pages.push(1);
                  
                  if (currentPage > 3) pages.push("...");
                  
                  const start = Math.max(2, currentPage - 1);
                  const end = Math.min(totalPages - 1, currentPage + 1);
                  
                  for (let i = start; i <= end; i++) {
                    if (!pages.includes(i)) pages.push(i);
                  }
                  
                  if (currentPage < totalPages - 2) pages.push("...");
                  
                  if (!pages.includes(totalPages)) pages.push(totalPages);
                }

                return pages.map((page, index) => (
                  page === "..." ? (
                    <span key={`dots-${index}`} style={{ display: "flex", alignItems: "center", color: theme.palette.text.secondary }}>
                      ...
                    </span>
                  ) : (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      style={{
                        width: "35px",
                        height: "35px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundColor: currentPage === page ? theme.palette.primary.main : theme.palette.background.paper,
                        color: currentPage === page ? theme.palette.primary.contrastText : theme.palette.text.primary,
                        border: `1px solid ${currentPage === page ? theme.palette.primary.main : theme.palette.divider}`,
                        borderRadius: "8px",
                        cursor: "pointer",
                        fontWeight: 700,
                        transition: "all 0.2s",
                      }}
                    >
                      {page}
                    </button>
                  )
                ));
              })()}
            </div>
            <button
              onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
              style={{
                padding: "8px 16px",
                backgroundColor: theme.palette.background.paper,
                color: currentPage === totalPages ? theme.palette.text.disabled : theme.palette.primary.main,
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: "8px",
                cursor: currentPage === totalPages ? "not-allowed" : "pointer",
                fontWeight: 700,
                transition: "all 0.2s",
              }}
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

export default Table
